/**
 * Raketech Post-Recon Comparison
 * Compares recon-period accounts against AC to find missing accounts
 * and revenue discrepancies after the AC migration.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Shield,
  Cloud,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Search,
  Key,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// AC token helpers (shared with ACCompare)
// ---------------------------------------------------------------------------
const AC_TOKEN_KEY = 'affiliation-cloud-token';
const AC_TOKEN_EXPIRY_HOURS = 12;

interface StoredToken { token: string; expiresAt: number }

const getStoredACToken = (): string | null => {
  try {
    const stored = localStorage.getItem(AC_TOKEN_KEY);
    if (stored) {
      const parsed: StoredToken = JSON.parse(stored);
      if (parsed.expiresAt > Date.now()) return parsed.token;
      localStorage.removeItem(AC_TOKEN_KEY);
    }
  } catch { localStorage.removeItem(AC_TOKEN_KEY); }
  return null;
};

const storeACToken = (token: string) => {
  localStorage.setItem(AC_TOKEN_KEY, JSON.stringify({
    token, expiresAt: Date.now() + AC_TOKEN_EXPIRY_HOURS * 3600 * 1000,
  }));
};

const getAdwstatsToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) return JSON.parse(auth).state?.token || null;
  } catch {}
  return null;
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ReconAccount {
  accountId: string;
  username: string;
  partner: string;
  brand: string;
  offer: string;
  geo: string;
  currency: string;
  sources: string[];
  reconEarnings: number;
  invoiceRevenue: number;
  partnerCommission: number;
  manualCheckAmount: number;
}

interface ACMatch {
  acId: number;
  name: string;
  advertiser: string;
  system: string;
  status: string;
  brands: string[];
}

interface PostReconRow {
  reconAccount: ReconAccount;
  acMatch: ACMatch | null;
  matchMethod: string;
  localAccountId?: string;
  postReconSynced: number;
  postReconSyncedRaw?: number;
  postReconCurrency?: string;
  acReportCommission: number;
  revenueDiff: number;
}

interface ExtraAC {
  acId: number;
  name: string;
  advertiser: string;
  system: string;
  status: string;
  brands: string[];
  acReportCommission: number;
}

interface PostReconData {
  period: { recon: string; postRecon: string };
  summary: {
    totalReconAccounts: number;
    totalAcAccounts: number;
    matched: number;
    missing: number;
    extraAcAccounts: number;
    withPostReconRevenue: number;
    withDiscrepancy: number;
    matchMethods: Record<string, number>;
  };
  rows: PostReconRow[];
  extraAcAccounts: ExtraAC[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);

type Tab = 'missing' | 'matched' | 'revenue' | 'extra';
type SortField = 'partner' | 'account' | 'reconEarnings' | 'postSynced' | 'acReport' | 'diff';
type SortDir = 'asc' | 'desc';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const RaketechPostRecon: React.FC = () => {
  const [acToken, setAcToken] = useState<string | null>(getStoredACToken());
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showTokenPaste, setShowTokenPaste] = useState(false);
  const [pastedToken, setPastedToken] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PostReconData | null>(null);

  const [tab, setTab] = useState<Tab>('missing');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('partner');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const isLoggedIn = !!acToken;

  // Login
  const handleLogin = useCallback(async () => {
    if (!email || !password) { toast.error('Enter email and password'); return; }
    setIsLoggingIn(true);
    try {
      const resp = await fetch('/api/ac-compare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'login', email, password }),
      });
      const d = await resp.json() as any;
      if (!d.success) throw new Error(d.error || 'Login failed');
      storeACToken(d.access_token);
      setAcToken(d.access_token);
      toast.success('Connected to Affiliation Cloud');
    } catch (err: any) { toast.error(err.message); }
    finally { setIsLoggingIn(false); }
  }, [email, password]);

  const handleTokenPaste = useCallback(() => {
    const trimmed = pastedToken.trim();
    if (!trimmed) return;
    storeACToken(trimmed);
    setAcToken(trimmed);
    setPastedToken('');
    toast.success('Token saved');
  }, [pastedToken]);

  const handleLogout = () => {
    localStorage.removeItem(AC_TOKEN_KEY);
    setAcToken(null);
    setData(null);
  };

  // Fetch comparison
  const fetchData = useCallback(async () => {
    if (!acToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const adwToken = getAdwstatsToken();
      const resp = await fetch('/api/raketech/post-recon', {
        headers: {
          'X-Affiliation-Cloud-Token': acToken,
          ...(adwToken ? { Authorization: `Bearer ${adwToken}` } : {}),
        },
      });
      const json = await resp.json() as any;
      if (!json.success) throw new Error(json.error || 'Comparison failed');
      setData(json.data);
    } catch (err: any) {
      setError(err.message);
      if (err.message.includes('401') || err.message.includes('token')) {
        handleLogout();
        toast.error('AC session expired, please log in again');
      }
    } finally { setIsLoading(false); }
  }, [acToken]);

  useEffect(() => { if (isLoggedIn) fetchData(); }, [isLoggedIn, fetchData]);

  // Filtered + sorted rows
  const filteredRows = useMemo(() => {
    if (!data) return [];
    let rows: PostReconRow[];
    switch (tab) {
      case 'missing': rows = data.rows.filter(r => !r.acMatch); break;
      case 'matched': rows = data.rows.filter(r => !!r.acMatch); break;
      case 'revenue': rows = data.rows.filter(r => !!r.acMatch && (r.postReconSynced > 0 || r.acReportCommission > 0) && Math.abs(r.revenueDiff) > 1); break;
      case 'extra': return []; // handled separately
      default: rows = data.rows;
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter(r =>
        r.reconAccount.partner?.toLowerCase().includes(q) ||
        r.reconAccount.brand?.toLowerCase().includes(q) ||
        r.reconAccount.accountId?.toLowerCase().includes(q) ||
        r.reconAccount.username?.toLowerCase().includes(q) ||
        r.reconAccount.offer?.toLowerCase().includes(q) ||
        r.acMatch?.name?.toLowerCase().includes(q) ||
        r.acMatch?.advertiser?.toLowerCase().includes(q)
      );
    }
    const getVal = (r: PostReconRow): number | string => {
      switch (sortField) {
        case 'partner': return (r.reconAccount.partner || '').toLowerCase();
        case 'account': return r.reconAccount.accountId;
        case 'reconEarnings': return r.reconAccount.reconEarnings;
        case 'postSynced': return r.postReconSynced;
        case 'acReport': return r.acReportCommission;
        case 'diff': return r.revenueDiff;
        default: return 0;
      }
    };
    return [...rows].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, tab, searchTerm, sortField, sortDir]);

  const filteredExtra = useMemo(() => {
    if (!data || tab !== 'extra') return [];
    let items = data.extraAcAccounts;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(a =>
        a.name?.toLowerCase().includes(q) ||
        a.advertiser?.toLowerCase().includes(q) ||
        String(a.acId).includes(q)
      );
    }
    return items;
  }, [data, tab, searchTerm]);

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const toggleRow = (key: string) => {
    setExpandedRows(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  };

  const SortTH: React.FC<{ field: SortField; label: string; right?: boolean }> = ({ field, label, right }) => (
    <th
      className={`px-3 py-2.5 font-medium cursor-pointer hover:text-zinc-300 select-none whitespace-nowrap text-zinc-500 ${right ? 'text-right' : 'text-left'}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </span>
    </th>
  );

  // -------------------------------------------------------------------------
  // Render: Not logged in
  // -------------------------------------------------------------------------
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
            <Shield className="h-7 w-7 text-orange-400" />
            Post-Recon AC Comparison
          </h1>
          <p className="text-zinc-400 mt-1 text-sm">Compare recon-period accounts against Affiliation Cloud to find missing accounts</p>
        </div>
        <div className="max-w-md mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-6">
          <div className="flex items-center gap-3 mb-6">
            <Cloud className="h-6 w-6 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">Connect to Affiliation Cloud</h2>
          </div>

          {!showTokenPaste ? (
            <>
              <div className="space-y-3">
                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="AC Email" className="w-full px-3 py-2 text-sm bg-[hsl(225,15%,14%)] border border-[hsl(225,15%,22%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50" />
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="AC Password" onKeyDown={e => e.key === 'Enter' && handleLogin()}
                    className="w-full px-3 py-2 pr-10 text-sm bg-[hsl(225,15%,14%)] border border-[hsl(225,15%,22%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50" />
                  <button onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <button onClick={handleLogin} disabled={isLoggingIn}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-500 rounded-lg transition-colors disabled:opacity-50">
                  {isLoggingIn ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : 'Connect'}
                </button>
              </div>
              <button onClick={() => setShowTokenPaste(true)} className="mt-3 text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1 mx-auto">
                <Key className="h-3 w-3" /> Or paste token directly
              </button>
            </>
          ) : (
            <div className="space-y-3">
              <textarea value={pastedToken} onChange={e => setPastedToken(e.target.value)}
                placeholder="Paste AC Bearer token..." rows={3}
                className="w-full px-3 py-2 text-xs bg-[hsl(225,15%,14%)] border border-[hsl(225,15%,22%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50 font-mono" />
              <div className="flex gap-2">
                <button onClick={handleTokenPaste} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-500 rounded-lg transition-colors">Save Token</button>
                <button onClick={() => setShowTokenPaste(false)} className="px-4 py-2 text-sm text-zinc-400 hover:text-white bg-[hsl(225,15%,14%)] rounded-lg border border-[hsl(225,15%,22%)]">Back</button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render: Logged in
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <Shield className="h-7 w-7 text-orange-400" />
              Post-Recon AC Comparison
            </h1>
            <p className="text-zinc-400 mt-1 text-sm">
              Recon accounts vs Affiliation Cloud — find missing accounts & revenue discrepancies
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
              <Cloud className="h-4 w-4 text-emerald-400" />
              <span className="text-xs text-emerald-400 font-medium">AC Connected</span>
            </div>
            <button onClick={fetchData} disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[hsl(225,15%,15%)] hover:bg-[hsl(225,15%,20%)] rounded-lg border border-[hsl(225,15%,22%)] transition-colors disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button onClick={handleLogout} className="px-3 py-2 text-xs text-zinc-500 hover:text-red-400 transition-colors">Disconnect</button>
          </div>
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-orange-400 mr-3" />
          <span className="text-zinc-400">Fetching AC accounts & comparing...</span>
        </div>
      )}

      {/* Error */}
      {error && !isLoading && (
        <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">Comparison Failed</h2>
          <p className="text-sm text-zinc-400">{error}</p>
        </div>
      )}

      {/* Main content */}
      {data && !isLoading && (
        <div className="space-y-6 animate-fade-in">
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Card label="Recon Accounts" value={String(data.summary.totalReconAccounts)} color="zinc" icon={<Search className="h-4 w-4" />} />
            <Card label="AC Accounts" value={String(data.summary.totalAcAccounts)} color="blue" icon={<Cloud className="h-4 w-4" />} />
            <Card label="Matched" value={String(data.summary.matched)} color="emerald" icon={<CheckCircle2 className="h-4 w-4" />} />
            <Card label="Missing from AC" value={String(data.summary.missing)} color="red" icon={<XCircle className="h-4 w-4" />} />
            <Card label="Revenue Discrepancies" value={String(data.summary.withDiscrepancy)} color="amber" icon={<AlertTriangle className="h-4 w-4" />} />
          </div>

          {/* Period info */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4 text-xs text-zinc-400 space-y-1">
            <p><strong className="text-zinc-300">Recon period:</strong> {data.period.recon}</p>
            <p><strong className="text-zinc-300">Post-recon period:</strong> {data.period.postRecon} (revenue comparison)</p>
            <p><strong className="text-zinc-300">Match methods:</strong> Mapping: {data.summary.matchMethods.mapping || 0}, AC ID: {data.summary.matchMethods.ac_id || 0}, Name pattern: {data.summary.matchMethods.name_pattern || 0}, Unmatched: {data.summary.matchMethods.unmatched || 0}</p>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-[hsl(225,15%,10%)] rounded-lg p-1 border border-[hsl(225,15%,18%)] w-fit">
            {([
              ['missing', `Missing (${data.summary.missing})`],
              ['matched', `Matched (${data.summary.matched})`],
              ['revenue', `Revenue Issues (${data.summary.withDiscrepancy})`],
              ['extra', `Extra AC (${data.summary.extraAcAccounts})`],
            ] as [Tab, string][]).map(([t, label]) => (
              <button key={t} onClick={() => { setTab(t); setSearchTerm(''); }}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${tab === t ? 'bg-[hsl(225,15%,18%)] text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>
                {label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="flex items-center gap-3">
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search partner, brand, account, offer..."
              className="px-3 py-1.5 w-72 text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50" />
            <span className="text-xs text-zinc-500">{tab === 'extra' ? filteredExtra.length : filteredRows.length} items</span>
          </div>

          {/* Table */}
          {tab !== 'extra' ? (
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(225,15%,8%)] sticky top-0">
                    <tr>
                      <th className="w-8 px-2 py-2.5"></th>
                      <SortTH field="partner" label="Partner / Brand" />
                      <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">GEO</th>
                      <SortTH field="account" label="Account" />
                      <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">Sources</th>
                      <SortTH field="reconEarnings" label="Recon Earnings" right />
                      {tab !== 'missing' && (
                        <>
                          <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">AC Match</th>
                          <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">Method</th>
                          <SortTH field="postSynced" label="Our Synced (Post)" right />
                          <SortTH field="acReport" label="AC Reported (Post)" right />
                          <SortTH field="diff" label="Diff" right />
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                    {filteredRows.map((row, idx) => {
                      const key = `${row.reconAccount.accountId}-${idx}`;
                      const isExpanded = expandedRows.has(key);
                      const ra = row.reconAccount;
                      return (
                        <React.Fragment key={key}>
                          <tr className="hover:bg-[hsl(225,15%,12%)] cursor-pointer" onClick={() => toggleRow(key)}>
                            <td className="px-2 py-2 text-zinc-500">
                              {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            </td>
                            <td className="px-3 py-2">
                              <div className="text-white font-medium">{ra.partner}</div>
                              <div className="text-zinc-500 text-[10px]">{ra.brand}{ra.offer ? ` — ${ra.offer}` : ''}</div>
                            </td>
                            <td className="px-3 py-2 text-purple-400 font-medium">{ra.geo}</td>
                            <td className="px-3 py-2">
                              <div className="text-zinc-300 tabular-nums">{ra.accountId}</div>
                              <div className="text-zinc-500 text-[10px] truncate max-w-[120px]">{ra.username}</div>
                            </td>
                            <td className="px-3 py-2">
                              <div className="flex flex-wrap gap-0.5">
                                {ra.sources.map(s => (
                                  <span key={s} className="px-1 py-0.5 rounded text-[9px] bg-zinc-700/50 text-zinc-400">{s.replace('_', ' ')}</span>
                                ))}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right text-zinc-300 tabular-nums">{ra.reconEarnings > 0 ? fmtCurrency(ra.reconEarnings) : '—'}</td>
                            {tab !== 'missing' && (
                              <>
                                <td className="px-3 py-2">
                                  {row.acMatch ? (
                                    <div>
                                      <div className="text-emerald-400 text-[10px] font-medium">{row.acMatch.name}</div>
                                      <div className="text-zinc-500 text-[9px]">{row.acMatch.advertiser}</div>
                                    </div>
                                  ) : (
                                    <span className="text-red-400 text-[10px]">Not found</span>
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium border ${
                                    row.matchMethod === 'mapping' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' :
                                    row.matchMethod === 'ac_id' ? 'text-blue-400 bg-blue-500/10 border-blue-500/20' :
                                    row.matchMethod === 'name_pattern' ? 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20' :
                                    'text-red-400 bg-red-500/10 border-red-500/20'
                                  }`}>{row.matchMethod}</span>
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{row.postReconSynced > 0 ? fmtCurrency(row.postReconSynced) : '—'}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{row.acReportCommission > 0 ? fmtCurrency(row.acReportCommission) : '—'}</td>
                                <td className={`px-3 py-2 text-right tabular-nums font-medium ${row.revenueDiff > 1 ? 'text-emerald-400' : row.revenueDiff < -1 ? 'text-red-400' : 'text-zinc-500'}`}>
                                  {Math.abs(row.revenueDiff) > 1 ? fmtCurrency(row.revenueDiff) : '—'}
                                </td>
                              </>
                            )}
                          </tr>

                          {/* Expanded detail */}
                          {isExpanded && (
                            <tr>
                              <td colSpan={tab === 'missing' ? 6 : 11} className="bg-[hsl(225,15%,8%)] px-6 py-3">
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                                  <div>
                                    <div className="text-zinc-500 text-[10px] uppercase mb-1">Recon Period Data</div>
                                    <div className="space-y-0.5">
                                      <div><span className="text-zinc-500">Recon Earnings:</span> <span className="text-white">{fmtCurrency(ra.reconEarnings)}</span></div>
                                      <div><span className="text-zinc-500">Invoice Revenue:</span> <span className="text-white">{fmtCurrency(ra.invoiceRevenue)}</span></div>
                                      <div><span className="text-zinc-500">Partner Commission:</span> <span className="text-white">{fmtCurrency(ra.partnerCommission)}</span></div>
                                      <div><span className="text-zinc-500">Manual Check:</span> <span className="text-white">{fmtCurrency(ra.manualCheckAmount)}</span></div>
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-zinc-500 text-[10px] uppercase mb-1">Account Info</div>
                                    <div className="space-y-0.5">
                                      <div><span className="text-zinc-500">Account ID:</span> <span className="text-white">{ra.accountId}</span></div>
                                      <div><span className="text-zinc-500">Username:</span> <span className="text-white">{ra.username || '—'}</span></div>
                                      <div><span className="text-zinc-500">Offer:</span> <span className="text-white">{ra.offer || '—'}</span></div>
                                      <div><span className="text-zinc-500">Currency:</span> <span className="text-white">{ra.currency}</span></div>
                                    </div>
                                  </div>
                                  {row.acMatch && (
                                    <div>
                                      <div className="text-zinc-500 text-[10px] uppercase mb-1">AC Account</div>
                                      <div className="space-y-0.5">
                                        <div><span className="text-zinc-500">AC ID:</span> <span className="text-white">{row.acMatch.acId}</span></div>
                                        <div><span className="text-zinc-500">Name:</span> <span className="text-white">{row.acMatch.name}</span></div>
                                        <div><span className="text-zinc-500">System:</span> <span className="text-white">{row.acMatch.system}</span></div>
                                        <div><span className="text-zinc-500">Status:</span> <span className={row.acMatch.status === 'active' ? 'text-emerald-400' : 'text-amber-400'}>{row.acMatch.status}</span></div>
                                        {row.acMatch.brands.length > 0 && (
                                          <div><span className="text-zinc-500">Brands:</span> <span className="text-white">{row.acMatch.brands.join(', ')}</span></div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {(row.postReconSynced > 0 || row.acReportCommission > 0) && (
                                    <div>
                                      <div className="text-zinc-500 text-[10px] uppercase mb-1">Post-Recon Revenue</div>
                                      <div className="space-y-0.5">
                                        <div><span className="text-zinc-500">Our Synced:</span> <span className="text-white">{fmtCurrency(row.postReconSynced)}</span>
                                          {row.postReconCurrency && row.postReconCurrency !== 'EUR' && (
                                            <span className="text-zinc-600 ml-1">({fmtCurrency(row.postReconSyncedRaw || 0)} {row.postReconCurrency})</span>
                                          )}
                                        </div>
                                        <div><span className="text-zinc-500">AC Reported:</span> <span className="text-white">{fmtCurrency(row.acReportCommission)}</span></div>
                                        <div><span className="text-zinc-500">Difference:</span> <span className={row.revenueDiff > 1 ? 'text-emerald-400' : row.revenueDiff < -1 ? 'text-red-400' : 'text-white'}>{fmtCurrency(row.revenueDiff)}</span></div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredRows.length === 0 && (
                <div className="p-8 text-center text-zinc-500 text-sm">
                  {tab === 'missing' ? 'All recon accounts found in AC' : tab === 'revenue' ? 'No revenue discrepancies detected' : 'No data'}
                </div>
              )}
            </div>
          ) : (
            /* Extra AC accounts table */
            <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-[hsl(225,15%,8%)] sticky top-0">
                    <tr>
                      <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">AC ID</th>
                      <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">Name</th>
                      <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">Advertiser</th>
                      <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">System</th>
                      <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">Status</th>
                      <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">Brands</th>
                      <th className="px-3 py-2.5 text-right text-zinc-500 font-medium">Post-Recon Commission</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                    {filteredExtra.map(ac => (
                      <tr key={ac.acId} className="hover:bg-[hsl(225,15%,12%)]">
                        <td className="px-3 py-2 text-zinc-300 tabular-nums">{ac.acId}</td>
                        <td className="px-3 py-2 text-white font-medium">{ac.name}</td>
                        <td className="px-3 py-2 text-zinc-300">{ac.advertiser}</td>
                        <td className="px-3 py-2 text-zinc-400">{ac.system}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${ac.status === 'active' ? 'text-emerald-400 bg-emerald-500/10' : 'text-amber-400 bg-amber-500/10'}`}>
                            {ac.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-zinc-400 text-[10px]">{ac.brands.join(', ') || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{ac.acReportCommission > 0 ? fmtCurrency(ac.acReportCommission) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredExtra.length === 0 && (
                <div className="p-8 text-center text-zinc-500 text-sm">No extra AC accounts found outside recon data</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const Card: React.FC<{ label: string; value: string; color: string; icon: React.ReactNode }> = ({ label, value, color, icon }) => {
  const cm: Record<string, string> = {
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    zinc: 'text-zinc-300 bg-[hsl(225,15%,12%)] border-[hsl(225,15%,18%)]',
  };
  const c = cm[color] || cm.zinc;
  const parts = c.split(' ');
  return (
    <div className={`rounded-xl border p-4 ${parts.slice(1).join(' ')}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={parts[0]}>{icon}</span>
        <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-lg sm:text-xl font-bold tabular-nums ${parts[0]}`}>{value}</p>
    </div>
  );
};

export default RaketechPostRecon;
